// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — yard generation. A yard is a PURE FUNCTION of (mode, seed): same seed, same yard, same
// par, on every peer and on every device. That is what makes the same-seed race honest and the
// daily yard the same yard for everybody, and nothing about a yard ever crosses the wire.
//
// Generation and par are one computation (see solve.ts): a Dijkstra outward from every finished
// position gives the exact optimal move count of every position in the yard. To make a puzzle we
// pick a position at the difficulty we want. So par is not an estimate attached to a puzzle
// afterwards — the puzzle is CHOSEN BY its par.
//
// Two constraints shape the pick:
//   • An OPENING, not just any position: the loco stands light on the headshunt and every wagon
//     is parked in a road. That is what a yard looks like at the start of a shift, and it means
//     the player's first decision is always "which road do I go to", never "what is this rake
//     doing coupled to me".
//   • An EVEN SPREAD of difficulty: far more positions sit at the deep end of a band than the
//     shallow end (measured: 858 openings at par 13 against 124 at par 7 in Sidings), so picking
//     uniformly across positions would quietly make almost every yard the hardest one available.
//     The par value is drawn first, then a yard from that par.

import { makeRng, shuffle } from '@ben-gy/game-engine/rng';
import { parValues, type Mode } from './modes';
import { keyOf, ROOT, type Move, type Yard } from './rules';
import { optimalLine, parseKey, searchFromFinished } from './solve';

export interface Puzzle {
  readonly mode: Mode;
  readonly seed: number;
  /** The opening position — the yard as the player first sees it. */
  readonly yard: Yard;
  /** The TRUE optimal move count. Proven, not estimated. */
  readonly par: number;
  /** One optimal line, for the results-screen replay of what everybody missed. */
  readonly line: readonly Move[];
  /** How many positions the search expanded — watched by the timing test. */
  readonly expanded: number;
}

/**
 * Build the yard for (mode, seed).
 *
 * Never throws and never returns an unsolvable yard: if the band somehow yields no opening (it
 * cannot for the shipped modes, and solve.test.ts asserts that across hundreds of seeds), the
 * deepest opening the search did find is used instead, and its par is still the proven optimum.
 */
export function generate(mode: Mode, seed: number): Puzzle {
  const rnd = makeRng(seed);
  const all = Array.from({ length: mode.cars }, (_, i) => i);

  // Which wagons the slip asks for, and in what order. This is where most of the variety lives:
  // the yard's shape is fixed by the mode, but the order slip is a fresh ordered selection.
  const target = shuffle(rnd, all).slice(0, mode.train);

  const search = searchFromFinished(mode.spec, target, all, {
    maxDist: mode.parMax,
    maxNodes: 400_000,
  });

  const isOpening = (y: Yard): boolean => y.locoAt === ROOT && y.cars[ROOT].length === 0;

  const bands = parValues(mode)
    .map((par) => ({
      par,
      keys: (search.byDist[par] ?? []).filter((k) => isOpening(parseKey(mode.spec, target, k))),
    }))
    .filter((b) => b.keys.length > 0);

  let chosenPar: number;
  let chosenKey: string;

  if (bands.length > 0) {
    const band = bands[Math.floor(rnd() * bands.length)];
    chosenPar = band.par;
    chosenKey = band.keys[Math.floor(rnd() * band.keys.length)];
  } else {
    // Belt and braces: fall back to the deepest opening the search reached at all.
    let best: { par: number; key: string } | null = null;
    for (let d = search.byDist.length - 1; d >= 1 && !best; d--) {
      for (const k of search.byDist[d]) {
        if (isOpening(parseKey(mode.spec, target, k))) {
          best = { par: d, key: k };
          break;
        }
      }
    }
    if (!best) {
      // Cannot happen for a shipped mode; a solvable-but-trivial yard beats throwing at round start.
      const finished = search.byDist[0][0];
      best = { par: 0, key: finished };
    }
    chosenPar = best.par;
    chosenKey = best.key;
  }

  const yard = parseKey(mode.spec, target, chosenKey);
  const line = optimalLine(yard, search.dist) ?? [];

  return { mode, seed, yard, par: chosenPar, line, expanded: search.expanded };
}

/** The key of a puzzle's opening — used by the determinism tests to compare two peers' yards. */
export function puzzleKey(p: Puzzle): string {
  return `${p.mode.id}|${p.par}|${p.yard.target.join('')}|${keyOf(p.yard)}`;
}
